import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { ArrowLeftIcon, ArrowUpRightIcon, BookOpenIcon } from 'lucide-react'

import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { Modal, brandText, brandTextHover } from '@repo/ui'

import { findDoc } from 'docs'
import { glossaryHref, glossaryTerm, type GlossaryEntry } from 'docs/glossary'
import { useGlossaryCorpus } from 'docs/GlossaryProvider'

import { TermProse } from '../TermProse'


/**
 * Singular kind labels for the header badge.
 *
 * Deliberately NOT `GLOSSARY_KINDS[].label`, which is plural because it heads a SECTION of
 * the glossary page ("Statuses", "Formulas"). This badge names what ONE entry is, and
 * "Activation trigger  TERMS" reads as a category tag on a single item — verified on screen
 * before changing it. Derived from the same `kind` union so a new kind is a type error here
 * rather than a blank badge.
 */
const KIND_LABEL: Record<GlossaryEntry['kind'], string> = {
  term: 'Term',
  status: 'Status',
  column: 'Column',
  formula: 'Formula',
}

/** `category/slug[#heading]` → a route and the guide's real title. */
const resolveDoc = (ref: string) => {
  const [path, heading] = ref.split('#')
  const [category, slug] = path.split('/')
  const entry = findDoc(category, slug)
  return {
    to: `/docs/${path}${heading ? `#${heading}` : ''}`,
    // Fall back to the slug rather than a generic "guide ↗". A row of identical links
    // tells the reader nothing about which one answers their question.
    title: entry?.title ?? slug?.replace(/-/g, ' ') ?? path,
  }
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontWeight: 700,
      color: 'text.secondary',
    }}
  >
    {children}
  </Typography>
)

interface Props {
  /** Glossary entry id the overlay opens on. */
  termId: string
  open: boolean
  onClose: () => void
}

/**
 * The full glossary entry, shown WITHOUT leaving the page.
 *
 * The problem it solves: the tooltip footer used to deep-link to
 * `/docs/reference/glossary#<id>`, so someone who wanted one more sentence about "Total
 * activation cost" was navigated off their billing page into the docs reader. They had a
 * question about the thing in front of them and the answer cost them their place — plus a
 * back-button press and a scroll position to re-find. §44 already rejected sending them to a
 * SEARCH page for this reason (one hop, not two); the remaining hop was still a full
 * navigation.
 *
 * ── Why the content swaps in place rather than stacking ──────────────────────────────
 *
 * `seeAlso` is a graph, and following a graph by opening a dialog per hop reaches three
 * levels almost immediately — which §52 rules out outright, and for reasons that apply here
 * exactly as they do to drawers: the backdrops compound until the page underneath is gone,
 * and every level adds another close control with no way to tell which one unwinds what.
 *
 * So there is ONE dialog and a trail. Following a related term replaces the body and pushes
 * onto `trail`; the header grows a back arrow that pops it. One surface, one close control,
 * and the reader can always get back to the term they actually asked about.
 *
 * ── Degrading when `long` is absent ─────────────────────────────────────────────────
 *
 * Most entries have no `long` today and some never will, so "no long" is the NORMAL case,
 * not an edge case. Every section here is independently conditional and the overlay simply
 * renders shorter: `short` is always present, and `hasExpandedEntry` guarantees at least one
 * other section exists or the tooltip would not have opened this at all. Nothing invents
 * placeholder copy to fill the space — an overlay padded with "No further detail available"
 * is worse than a short one, because it makes the reader read a sentence to learn nothing.
 */
export const TermOverlay = ({ termId, open, onClose }: Props) => {
  const theme = useTheme()

  // Last element is what's on screen. A stack rather than a single id so the back arrow can
  // walk a chain of related terms rather than only ever returning to the origin.
  const [trail, setTrail] = useState<string[]>([termId])

  // Reset on each OPEN, not on every `termId` change. Resetting on the prop alone would be
  // wrong while the dialog is open — nothing changes it mid-flight — and resetting only on
  // mount would be wrong too, since the tooltip keeps this mounted between openings, so the
  // second open would resume someone else's abandoned trail.
  useEffect(() => {
    if (open) setTrail([termId])
  }, [open, termId])

  const currentId = trail[trail.length - 1] ?? termId
  // The reader's own corpus, so staff can walk a trail THROUGH internal terms and a brand
  // cannot. `related` below filters to what resolves here, which is what keeps the trail
  // inside the reader's audience without a second check.
  const corpus = useGlossaryCorpus()
  const entry = glossaryTerm(currentId, corpus)

  const goTo = useCallback((id: string) => setTrail(t => [...t, id]), [])
  const goBack = useCallback(() => setTrail(t => (t.length > 1 ? t.slice(0, -1) : t)), [])

  // Resolved here rather than in the render so a dangling id is dropped once. The publisher
  // build-enforces that `seeAlso` resolves, but this component would render a blank row if
  // that ever regressed, and a blank row is harder to notice than a missing one.
  const related = useMemo(
    () =>
      (entry?.seeAlso ?? [])
        .map(id => glossaryTerm(id, corpus))
        .filter((e): e is GlossaryEntry => Boolean(e)),
    [entry, corpus],
  )

  const guides = useMemo(() => (entry?.docs ?? []).map(resolveDoc), [entry])

  // Unreachable in practice, and deliberately so at BOTH ends: `InfoTooltip` only renders
  // this once its own lookup resolved, and `related` filters to resolved entries before
  // rendering, so nothing unresolvable can be pushed onto the trail. Kept because the
  // alternative failure — an open dialog rendering a header with an empty body — is worse
  // than no dialog, and because `glossaryTerm` is typed as possibly-undefined regardless.
  if (!entry) return null

  const kindLabel = KIND_LABEL[entry.kind]

  // ONE size for every link in the body, Related and Guides alike.
  //
  // They rendered at different sizes and neither was chosen: Related is a `component="button"`
  // carrying `font: 'inherit'`, which is a SHORTHAND and resets font-size — so despite an
  // explicit `fontSize: 14` alongside it, the buttons inherited the dialog's 16px while the
  // Guides anchors stayed at 14. Two peer lists, two sizes, by accident. Fixed by deleting
  // the shorthand at the call site so both read this value.
  const linkSx = {
    fontSize: 16,
    color: brandText(theme),
    textDecoration: 'none',
    '&:hover': { color: brandTextHover(theme), textDecoration: 'underline' },
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={entry.term}
      // §52's "the header's affordance changes with the step". A back arrow LEFT of the term
      // name, in the slot `Modal` reserves for an identity glyph, so the header reads as the
      // same object it always was — one dialog, showing a different term.
      titleIcon={
        trail.length > 1 ? (
          <IconButton
            onClick={goBack}
            aria-label="Back to the previous term"
            size="small"
            sx={{ ml: -1, color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary } }}
          >
            <ArrowLeftIcon size={18} strokeWidth={1.5} />
          </IconButton>
        ) : undefined
      }
      titleAdornment={
        kindLabel ? (
          <Typography
            sx={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 700,
              color: 'text.secondary',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              px: 2,
              py: '2px',
            }}
          >
            {kindLabel}
          </Typography>
        ) : undefined
      }
      bodySx={{ pb: 5 }}
      actions={
        // The escape hatch, deliberately quiet and deliberately NOT the main path: someone
        // who wants to browse the whole vocabulary should be able to get there, but the
        // reader this overlay is built for wanted one definition and is already done. Making
        // this prominent would rebuild the three-step chain the overlay exists to collapse.
        //
        // Anchored on the term CURRENTLY shown, not the one that opened the dialog — after
        // two hops through `seeAlso`, "see all terms" means "…starting from where I am now".
        // A TERTIARY BUTTON, matching the ⌘K palette's footer bar ("Ask Vera", "See docs",
        // "Product tours" — `variant="text"`, `size="small"`, a 16px leading glyph, 13px
        // label). Those two footers answer the same question, "I am done here, where next",
        // so they should not be a bare underlined link in one place and a button row in the
        // other. It stays TEXT variant rather than outlined: quiet is still the point (see
        // above) — a button affordance, not a promoted action.
        <Button
          component={RouterLink}
          to={glossaryHref(currentId)}
          onClick={onClose}
          variant="text"
          size="small"
          disableElevation
          startIcon={<BookOpenIcon strokeWidth={1} size={16} />}
          sx={{ textTransform: 'none', fontSize: 13, color: 'text.secondary' }}
        >
          See all terms
        </Button>
      }
    >
      {/*
        The dialog region. `role`/`aria-modal`/`aria-label` sit here rather than on the
        focus-trapped container because that container belongs to `@repo/ui`'s `Modal`, which
        is shared with ~30 other call sites and is not this component's to change. See the
        note in the final report: the shared Modal passes `aria-labelledby={title}` — the
        title STRING, not an element id — so it resolves to nothing, and it sets no `role`.
        That is a layer-1 gap worth fixing centrally rather than papering over per call site.

        `key` on the swap so a content change re-runs the fade. Without it React reuses the
        subtree and the new term appears with no transition at all, which reads as a glitch
        rather than a step.
      */}
      <Box
        key={currentId}
        role="dialog"
        aria-modal="true"
        aria-label={`${entry.term} — glossary definition`}
        sx={{
          '@keyframes termSwapIn': {
            from: { opacity: 0, transform: 'translateX(6px)' },
            to: { opacity: 1, transform: 'translateX(0)' },
          },
          animation: 'termSwapIn 180ms cubic-bezier(0.32, 0.72, 0, 1)',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <Stack spacing={4}>
          {entry.aliases && entry.aliases.length > 0 && (
            <Typography sx={{ fontSize: 14, mt: -1 }} color="text.secondary">
              also: {entry.aliases.join(', ')}
            </Typography>
          )}

          {/* Same 16/1.75 the glossary page gives this sentence. It is the same sentence —
              read from the same field — so it should not change size depending on where the
              reader met it. */}
          <Typography sx={{ fontSize: 16, lineHeight: 1.75 }}>{entry.short}</Typography>

          {/*
            `long` is MARKDOWN, so a definition can reference another term mid-sentence
            rather than only via the Related chips below (§44's authored form). A term
            here calls `goTo` — NOT `Term`, which would open a second `TermOverlay`
            inside the one already on screen, the self-stacking §52 forbids and this
            component's own docblock rules out. So both routes to a related term do the
            same thing: swap the body in place and push onto the trail.
          */}
          {entry.long && (
            <TermProse
              text={entry.long}
              onTermClick={goTo}
              sx={{ fontSize: 16, lineHeight: 1.75, color: 'text.secondary' }}
            />
          )}

          {entry.formula && (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 3,
                borderRadius: 2,
                // WRAPS, unlike the glossary page's copy of this block, which is
                // `overflowX: auto` only. In a 533px dialog that clipped the one live
                // formula mid-word ("…less your included allow|") behind a scrollbar with no
                // visible affordance — measured on screen. These "formulas" are written as
                // prose sentences rather than expressions, so wrapping at spaces loses
                // nothing; `overflowX` stays as the backstop for a genuinely unbreakable
                // token. The glossary page has the same clipping on a narrow viewport and is
                // not this component's to change.
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowX: 'auto',
                lineHeight: 1.6,
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
                fontSize: 14,
              }}
            >
              {entry.formula}
              {entry.unit ? `   [${entry.unit}]` : ''}
            </Box>
          )}

          {related.length > 0 && (
            <Stack spacing={2}>
              <SectionLabel>Related</SectionLabel>
              <Stack direction="row" flexWrap="wrap" sx={{ gap: 3 }}>
                {related.map(r => (
                  // A BUTTON, not a link: activating it swaps this dialog's body, it does not
                  // navigate. Styling it as a link would promise a destination and a
                  // middle-clickable URL that do not exist here — the term's own URL is what
                  // "See all terms" in the footer is for.
                  <Typography
                    key={r.id}
                    component="button"
                    type="button"
                    onClick={() => goTo(r.id)}
                    sx={{
                      ...linkSx,
                      background: 'none',
                      border: 'none',
                      p: 0,
                      // NOT `font: 'inherit'`. It is a shorthand, so it resets font-size and
                      // silently beat the `fontSize` beside it — which is how these buttons
                      // ended up 16px while the Guides links next to them were 14px. Reset
                      // only the properties a <button> actually needs resetting.
                      fontFamily: 'inherit',
                      fontWeight: 'inherit',
                      lineHeight: 'inherit',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {r.term}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          )}

          {guides.length > 0 && (
            <Stack spacing={2}>
              <SectionLabel>Guides</SectionLabel>
              {/*
                The SAME row treatment as Related above — `direction="row"`, `flexWrap`,
                `gap: 3` — not a third spacing value invented for this list.

                It was a `column` with `spacing={2}`, which cost 56px of dialog height for
                two links that measured 77.6 and 59.8 wide inside a 499px row: a
                one-per-line list in a column with 360px of unused width, directly beneath
                a peer list that wraps. Two lists of short labels, one wrapping and one
                stacked, read as different KINDS of thing when they are the same kind.

                They stay two labelled sections rather than merging into one row (which is
                what the glossary PAGE does): the section labels are what say which is a
                term and which is a document, and the `↗` on a guide says it leaves the
                page while a term swaps this dialog's body in place.
              */}
              <Stack direction="row" flexWrap="wrap" sx={{ gap: 3 }}>
                {guides.map(g => (
                  <Typography
                    key={g.to}
                    component={RouterLink}
                    to={g.to}
                    onClick={onClose}
                    sx={{ ...linkSx, display: 'inline-flex', alignItems: 'center', gap: 1 }}
                  >
                    {g.title}
                    <ArrowUpRightIcon size={12} strokeWidth={1.5} />
                  </Typography>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Box>
    </Modal>
  )
}
